import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiBox,
  FiDroplet,
  FiGrid,
  FiHome,
  FiLayers,
  FiMaximize2,
  FiPackage,
  FiPlus,
  FiSun,
  FiTruck,
  FiArrowRight,
  FiDivide,
  FiFileMinus,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";

const masterModules = [
  {
    key: "brands",
    title: "Brands",
    endpoint: "/brands",
    path: "/admindashboard/masters/brands",
    icon: FiBox,
    color: "bg-blue-50 text-blue-600",
  },
  {
    key: "categories",
    title: "Categories",
    endpoint: "/categories",
    path: "/admindashboard/masters/categories",
    icon: FiGrid,
    color: "bg-purple-50 text-purple-600",
  },
  {
    key: "subcategories",
    title: "Subcategories",
    endpoint: "/subcategories",
    path: "/admindashboard/masters/subcategories",
    icon: FiLayers,
    color: "bg-pink-50 text-pink-600",
  },
  {
    key: "colors",
    title: "Colors",
    endpoint: "/colors",
    path: "/admindashboard/masters/colors",
    icon: FiDroplet,
    color: "bg-red-50 text-red-600",
  },
  {
    key: "sizeGroups",
    title: "Size Groups",
    endpoint: "/size-groups",
    path: "/admindashboard/masters/size-groups",
    icon: FiMaximize2,
    color: "bg-indigo-50 text-indigo-600",
  },
  {
    key: "sizes",
    title: "Sizes",
    endpoint: "/sizes",
    path: "/admindashboard/masters/sizes",
    icon: FiDivide,
    color: "bg-green-50 text-green-600",
  },
  {
    key: "units",
    title: "Units",
    endpoint: "/units",
    path: "/admindashboard/masters/units",
    icon: FiFileMinus,
    color: "bg-cyan-50 text-cyan-600",
  },
  {
    key: "seasons",
    title: "Seasons",
    endpoint: "/seasons",
    path: "/admindashboard/masters/seasons",
    icon: FiSun,
    color: "bg-yellow-50 text-yellow-600",
  },
  {
    key: "suppliers",
    title: "Suppliers",
    endpoint: "/suppliers",
    path: "/admindashboard/masters/suppliers",
    icon: FiTruck,
    color: "bg-orange-50 text-orange-600",
  },
  {
    key: "branches",
    title: "Branches",
    endpoint: "/branches",
    path: "/admindashboard/masters/branches",
    icon: FiHome,
    color: "bg-violet-50 text-violet-600",
  },
  {
    key: "stores",
    title: "Stores & Warehouses",
    endpoint: "/stores",
    path: "/admindashboard/masters/stores",
    icon: FiHome,
    color: "bg-sky-50 text-sky-600",
  },
  {
    key: "products",
    title: "Products",
    endpoint: "/products",
    path: "/admindashboard/masters/products",
    icon: FiPackage,
    color: "bg-primary-50 text-primary-700",
  },
];

const displayName = (record) =>
  record.displayName ||
  record.name ||
  record.fullName ||
  record.code ||
  "Master record";

const relativeTime = (dateValue) => {
  if (!dateValue) return "Recently";

  const difference = Date.now() - new Date(dateValue).getTime();
  const minutes = Math.max(0, Math.floor(difference / 60000));

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

export default function MastersDashboard() {
  const navigate = useNavigate();
  const { accessToken, isLoading: isAuthLoading } = useAuth();

  const [moduleData, setModuleData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      setError("");

      const responses = await Promise.all(
        masterModules.map((module) =>
          apiRequest(module.endpoint, { accessToken }),
        ),
      );

      const nextData = {};

      masterModules.forEach((module, index) => {
        nextData[module.key] = Array.isArray(responses[index])
          ? responses[index]
          : [];
      });

      setModuleData(nextData);
    } catch (err) {
      setError(err.message || "Unable to load masters dashboard.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;

    loadDashboard();
  }, [isAuthLoading, accessToken, loadDashboard]);

  const modules = useMemo(
    () =>
      masterModules.map((module) => ({
        ...module,
        records: moduleData[module.key] || [],
        count: (moduleData[module.key] || []).length,
      })),
    [moduleData],
  );

  const totalMasters = modules.reduce(
    (total, module) => total + module.count,
    0,
  );

  const activeBrands = (moduleData.brands || []).filter(
    (brand) => brand.isActive,
  ).length;

  const recentUpdates = useMemo(() => {
    return modules
      .flatMap((module) =>
        module.records.map((record) => ({
          id: `${module.key}-${record.id}`,
          title: module.title,
          name: displayName(record),
          createdAt: record.updatedAt || record.createdAt,
        })),
      )
      .filter((item) => item.createdAt)
      .sort(
        (first, second) =>
          new Date(second.createdAt).getTime() -
          new Date(first.createdAt).getTime(),
      )
      .slice(0, 5);
  }, [modules]);

  return (
    <div>
      <div className="mb-8">
        <p className="text-sm text-gray-500">ERP / Masters</p>

        <h1 className="mt-1 text-3xl font-bold text-gray-900">
          Masters Dashboard
        </h1>

        <p className="mt-2 text-gray-500">
          Manage catalogue, supplier, branch, store, and product master records.
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-2xl bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total Master Records"
          value={loading ? "—" : totalMasters}
        />

        <StatCard
          label="Active Brands"
          value={loading ? "—" : activeBrands}
          valueColor="text-green-600"
        />

        <StatCard
          label="Suppliers"
          value={loading ? "—" : (moduleData.suppliers || []).length}
        />

        <StatCard
          label="Stores & Warehouses"
          value={loading ? "—" : (moduleData.stores || []).length}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-4">
        <div className="xl:col-span-3">
          <div className="rounded-3xl border border-gray-100 bg-white p-6">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Master Modules</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Open a module to manage its records.
                </p>
              </div>

              <button
                onClick={loadDashboard}
                className="text-sm font-medium text-primary-700"
              >
                Refresh
              </button>
            </div>

            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {modules.map((module) => {
                const Icon = module.icon;

                return (
                  <button
                    key={module.key}
                    onClick={() => navigate(module.path)}
                    className="rounded-3xl border border-gray-100 bg-gray-50 p-5 text-left transition-all hover:-translate-y-1 hover:bg-white hover:shadow-lg"
                  >
                    <div
                      className={`mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${module.color}`}
                    >
                      <Icon size={24} />
                    </div>

                    <h3 className="font-semibold text-gray-900">
                      {module.title}
                    </h3>

                    <p className="mt-1 text-sm text-gray-500">
                      {loading ? "Loading…" : `${module.count} Records`}
                    </p>

                    <div className="mt-4 flex items-center gap-2 text-sm font-medium text-primary-700">
                      Open Module
                      <FiArrowRight />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-3xl border border-gray-100 bg-white p-6">
            <h2 className="mb-5 text-lg font-semibold">Quick Actions</h2>

            <div className="space-y-3">
              <QuickAction
                label="Manage Brands"
                primary
                onClick={() =>
                  navigate("/admindashboard/masters/brands")
                }
              />

              <QuickAction
                label="Manage Suppliers"
                onClick={() =>
                  navigate("/admindashboard/masters/suppliers")
                }
              />

              <QuickAction
                label="Create Product"
                onClick={() =>
                  navigate("/admindashboard/masters/products/add")
                }
              />
            </div>
          </div>

          <div className="rounded-3xl border border-gray-100 bg-white p-6">
            <h2 className="mb-5 text-lg font-semibold">Recent Updates</h2>

            {loading ? (
              <p className="text-sm text-gray-500">Loading updates…</p>
            ) : recentUpdates.length === 0 ? (
              <p className="text-sm text-gray-500">
                Create master records to see activity here.
              </p>
            ) : (
              <div className="space-y-4">
                {recentUpdates.map((item) => (
                  <div key={item.id}>
                    <p className="text-sm font-medium text-gray-900">
                      {item.title} updated
                    </p>

                    <p className="mt-1 text-xs text-gray-500">
                      {item.name} · {relativeTime(item.createdAt)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-3xl bg-primary-700 p-6 text-white">
            <h2 className="text-lg font-semibold">ERP Setup</h2>

            <p className="mt-2 text-sm text-white/80">
              Masters are now ready. Add products, configure opening stock,
              then continue to Purchase Orders.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, valueColor = "text-gray-900" }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <p className="text-sm text-gray-500">{label}</p>
      <h2 className={`mt-2 text-3xl font-bold ${valueColor}`}>{value}</h2>
    </div>
  );
}

function QuickAction({ label, onClick, primary = false }) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm font-medium ${
        primary
          ? "bg-primary-700 text-white"
          : "bg-gray-50 text-gray-700 hover:bg-gray-100"
      }`}
    >
      <FiPlus />
      {label}
    </button>
  );
}